import React, { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  Box,
  CircularProgress,
  Dialog,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"

import {
  getOrganizationsEnterpriseSelector,
  getCatalogProductTypesSelector,
} from "../../../../reducers/Organizations/organizations"
import { saveAndSyncEnterprisePackagingSettings } from "../../../../actions/Organizations/Enterprise"

const PackagingSettingsEditModal = ({ open, setting, onClose, onSave }) => {
  const dispatch = useDispatch()
  const enterprise = useSelector(getOrganizationsEnterpriseSelector)
  const catalogProductTypes = useSelector(getCatalogProductTypesSelector)
  const packagings = useSelector((state) => state.packagingList.packagingList)

  const enterprisePackagings = packagings.filter(p =>
    (p.toJSON().enterprises || []).some(e => e.objectId === enterprise?.objectId)
  )
  const filteredPackagings = enterprisePackagings.filter(p => p.get("type") === "1" || p.get("type") === "0")
  const filteredSubPackagings = enterprisePackagings.filter(p => p.get("type") === "2" || p.get("type") === "0")

  const getLabel = (kfcName) => {
    const pt = catalogProductTypes.find((p) => p.kfcName === kfcName)
    return pt ? pt.label : kfcName
  }

  const [packagingId, setPackagingId] = useState(setting.packagingId || "")
  const [subPackagingId, setSubPackagingId] = useState(setting.subPackagingId || "")
  const [dlc, setDlc] = useState(setting.dlc || "")
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  const handleSave = async () => {
    if (!packagingId && !subPackagingId && !dlc) {
      setError("Renseignez au moins un champ.")
      return
    }
    if (dlc) {
      const dlcNum = Number(dlc)
      if (!Number.isInteger(dlcNum) || dlcNum < 1) {
        setError("La DLC doit être un entier d'au moins 1 jour.")
        return
      }
    }
    setError(null)
    setBusy(true)
    const other = (enterprise?.packagingSettings || []).filter((s) => s.productType !== setting.productType)
    const next = [
      ...other,
      { productType: setting.productType, packagingId: packagingId || null, subPackagingId: subPackagingId || null, dlc: dlc ? Number(dlc) : null },
    ]
    const result = await dispatch(saveAndSyncEnterprisePackagingSettings(enterprise.objectId, next))
    setBusy(false)
    if (result !== false) onSave()
  }

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: sx.paper }} maxWidth={false}>
      {/* create-modal-header */}
      <Box sx={sx.header}>
        <Typography sx={sx.title}>Modifier — {getLabel(setting.productType)}</Typography>
        <Box component="button" onClick={onClose} aria-label="Fermer" sx={sx.closeBtn}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </Box>
      </Box>

      {/* create-modal-body — gap 32px between fields, padding 32px 40px 8px */}
      <Box sx={sx.body}>
        {/* Type de produits — disabled select with floating label */}
        <Box>
          <FormControl variant="standard" fullWidth>
            <Box sx={sx.floatLabel}>Type de produits</Box>
            <Select value={setting.productType} disabled sx={sx.select}>
              <MenuItem value={setting.productType}>{getLabel(setting.productType)}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Packaging */}
        <Box>
          <FormControl variant="standard" fullWidth>
            <Box sx={sx.floatLabel}>Packaging</Box>
            <Select
              value={packagingId}
              onChange={(e) => setPackagingId(e.target.value)}
              displayEmpty
              sx={sx.select}
            >
              <MenuItem value=""><em style={{ color: "#9B9B9B" }}>— Choisir —</em></MenuItem>
              {filteredPackagings.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.get("name")}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Sous-packaging */}
        <Box>
          <FormControl variant="standard" fullWidth>
            <Box sx={sx.floatLabel}>Sous-packaging</Box>
            <Select
              value={subPackagingId}
              onChange={(e) => setSubPackagingId(e.target.value)}
              displayEmpty
              sx={sx.select}
            >
              <MenuItem value="" sx={{ color: "#9B9B9B" }}>Aucun — par défaut</MenuItem>
              {filteredSubPackagings.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.get("name")}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* DLC — cm-float pattern */}
        <Box sx={sx.cmFloat}>
          <Box component="label" htmlFor="pc-dlc" sx={sx.cmLabel}>
            DLC (nombre de jours)
          </Box>
          <Box
            id="pc-dlc"
            component="input"
            type="number"
            min={1}
            max={90}
            step={1}
            value={dlc}
            onChange={(e) => setDlc(e.target.value)}
            sx={sx.cmInput}
          />
        </Box>

        {error && <Typography sx={sx.errorText}>{error}</Typography>}
      </Box>

      {/* create-modal-footer */}
      <Box sx={sx.footer}>
        <Box component="button" onClick={onClose} disabled={busy} sx={sx.btnText}>
          Annuler
        </Box>
        <Box component="button" onClick={handleSave} disabled={busy} sx={sx.btnFilled}>
          {busy ? <CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} /> : null}
          Enregistrer
        </Box>
      </Box>
    </Dialog>
  )
}

const sx = {
  paper: {
    width: 560,
    maxWidth: "calc(100vw - 32px)",
    maxHeight: "calc(100vh - 64px)",
    borderRadius: "12px",
    boxShadow: "0 12px 40px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    px: "40px",
    pt: "32px",
    pb: "8px",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "16px",
    flexShrink: 0,
  },
  title: {
    fontSize: 20,
    fontWeight: 400,
    color: "rgba(0,0,0,0.87)",
    lineHeight: 1.3,
  },
  closeBtn: {
    width: 36,
    height: 36,
    border: "none",
    background: "transparent",
    color: "#555555",
    cursor: "pointer",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mt: "-8px",
    mr: "-8px",
    flexShrink: 0,
    transition: "background 150ms",
    "&:hover": { bgcolor: "#F0F0F0" },
  },
  body: {
    px: "40px",
    pt: "32px",
    pb: "8px",
    overflowY: "auto",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "32px",
  },
  floatLabel: {
    fontSize: 12,
    color: "#7C7C7C",
    mb: "4px",
    lineHeight: 1,
  },
  select: {
    fontSize: 16,
    "& .MuiSelect-select": { pb: "8px" },
  },
  cmFloat: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  cmLabel: {
    fontSize: 14,
    fontWeight: 400,
    color: "#757575",
    lineHeight: 1,
  },
  cmInput: {
    width: "100%",
    border: "none",
    borderBottom: "1px solid #E0E0E0",
    background: "transparent",
    fontFamily: "inherit",
    fontSize: 14,
    color: "rgba(0,0,0,0.87)",
    p: "8px 0",
    outline: "none",
    borderRadius: 0,
    appearance: "none",
    "&:focus": { borderBottom: "2px solid #2196F3" },
  },
  errorText: {
    color: "error.main",
    fontSize: 13,
    mt: "-16px",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",
    px: "40px",
    pt: "24px",
    pb: "32px",
    flexShrink: 0,
  },
  btnText: {
    background: "transparent",
    border: "none",
    color: "#2196F3",
    fontFamily: "inherit",
    fontSize: 14,
    fontWeight: 500,
    px: "28px",
    py: "12px",
    borderRadius: "4px",
    cursor: "pointer",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    ml: "-28px",
    transition: "background 150ms",
    "&:hover": { bgcolor: "#E3F2FD" },
    "&:disabled": { opacity: 0.5, cursor: "default" },
  },
  btnFilled: {
    background: "#2196F3",
    border: "none",
    color: "#fff",
    fontFamily: "inherit",
    fontSize: 14,
    fontWeight: 500,
    px: "28px",
    py: "12px",
    borderRadius: "4px",
    cursor: "pointer",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    boxShadow: "0 2px 4px rgba(33,150,243,0.3)",
    display: "inline-flex",
    alignItems: "center",
    transition: "box-shadow 150ms, background 150ms",
    "&:hover": { boxShadow: "0 4px 8px rgba(33,150,243,0.4)" },
    "&:disabled": { opacity: 0.5, cursor: "default" },
  },
}

export default PackagingSettingsEditModal
